/**
 * Drift-compensated countdown timer.
 *
 * setInterval drifts a few ms per tick — over 5 minutes that's not enough to matter
 * visually, but using Date.now() anchored math keeps the display rock-solid even when
 * the tab is throttled in the background.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

interface TimerOptions {
  durationMs: number;
  onTick?: (remainingMs: number) => void;
  onFinish?: () => void;
}

export function useTimer({ durationMs, onTick, onFinish }: TimerOptions) {
  const [remaining, setRemaining] = useState(durationMs);
  const [running, setRunning] = useState(false);
  const endAtRef = useRef<number | null>(null);
  const finishedRef = useRef(false);

  const tick = useCallback(() => {
    if (!endAtRef.current) return;
    const ms = Math.max(0, endAtRef.current - Date.now());
    setRemaining(ms);
    if (onTick) onTick(ms);
    if (ms <= 0 && !finishedRef.current) {
      finishedRef.current = true;
      setRunning(false);
      onFinish?.();
    }
  }, [onTick, onFinish]);

  useEffect(() => {
    if (!running) return;
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [running, tick]);

  const start = useCallback(() => {
    if (running) return;
    finishedRef.current = false;
    endAtRef.current = Date.now() + remaining;
    setRunning(true);
  }, [remaining, running]);

  const stop = useCallback(() => {
    setRunning(false);
    endAtRef.current = null;
  }, []);

  const reset = useCallback(() => {
    finishedRef.current = false;
    setRunning(false);
    endAtRef.current = null;
    setRemaining(durationMs);
  }, [durationMs]);

  /** Force-complete (e.g. user pressed "我已经做完了") */
  const complete = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setRunning(false);
    endAtRef.current = null;
    setRemaining(0);
    onFinish?.();
  }, [onFinish]);

  return { remaining, running, start, stop, reset, complete };
}

export function formatRemaining(ms: number): { m: string; s: string; total: string } {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  const mm = m.toString().padStart(1, '0');
  const ss = s.toString().padStart(2, '0');
  return { m: mm, s: ss, total: `${mm}:${ss}` };
}
